import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchWorkerRoster, upsertWorkerRoster } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { fieldControlClass, fieldLabelClass } from '../components/ui/forms';

// Saved, reusable, non-account workers a Field Lead picks from when
// building a crew (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md sec9.3/9.4).
// Workers listed here never receive app accounts or logins.
export function WorkerRosterPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['workerRoster'], queryFn: fetchWorkerRoster });
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [dayRate, setDayRate] = useState('');

  const addWorker = useMutation({
    mutationFn: () => upsertWorkerRoster({ name, phone, defaultDayRate: Number(dayRate) || 0 }),
    onSuccess: (result) => {
      if (result.ok) {
        setName('');
        setPhone('');
        setDayRate('');
        void queryClient.invalidateQueries({ queryKey: ['workerRoster'] });
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    addWorker.mutate();
  }

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Field Operations"
        title="Worker Roster"
        actions={
          <Link to="/field" className="text-sm font-semibold text-gold-300 hover:text-gold-200">
            Back to Assigned Jobs
          </Link>
        }
      />

      <Card>
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
          <div className="min-w-[10rem] flex-1 space-y-1">
            <label htmlFor="worker-name" className={fieldLabelClass}>
              Name
            </label>
            <input id="worker-name" className={fieldControlClass} value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="w-36 space-y-1">
            <label htmlFor="worker-phone" className={fieldLabelClass}>
              Phone
            </label>
            <input id="worker-phone" className={fieldControlClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="w-28 space-y-1">
            <label htmlFor="worker-rate" className={fieldLabelClass}>
              Day Rate
            </label>
            <input id="worker-rate" type="number" min="0" step="1" className={fieldControlClass} value={dayRate} onChange={(e) => setDayRate(e.target.value)} />
          </div>
          <Button type="submit" busy={addWorker.isPending} busyLabel="Saving…">
            Add Worker
          </Button>
        </form>
      </Card>

      {addWorker.isError && (
        <ErrorState message={addWorker.error instanceof Error ? addWorker.error.message : 'Could not save this worker.'} />
      )}
      {addWorker.data && !addWorker.data.ok && <ErrorState message={addWorker.data.message || 'Could not save this worker.'} />}

      {query.isLoading && <LoadingState label="Loading worker roster…" />}
      {query.isError && (
        <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load the worker roster.'} onRetry={() => query.refetch()} />
      )}
      {query.data && !query.data.ok && <ErrorState message={query.data.message || 'Could not load the worker roster.'} onRetry={() => query.refetch()} />}

      {query.data?.ok && (query.data.workers?.length ?? 0) === 0 && <EmptyState>No workers saved yet. Add one above.</EmptyState>}

      {query.data?.ok && (query.data.workers?.length ?? 0) > 0 && (
        <ul className="space-y-2">
          {query.data.workers!.map((w) => (
            <li key={w.workerId} className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink-surface p-3.5 shadow-card">
              <div>
                <p className="font-semibold text-white">{w.name}</p>
                <p className="text-sm text-slate-400">{w.phone || '—'}</p>
              </div>
              {w.defaultDayRate > 0 && <span className="text-sm font-semibold text-gold-200">${w.defaultDayRate}/day</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
